/**
 * Panel de KPIs del taller (REVIX) consumiendo /api/insurama/kpis.
 * Se muestra como pestaña dentro de la página Insurama.
 */
import { useEffect, useState } from 'react';
import {
  BarChart3, RefreshCw, TrendingUp, Package, Clock, Shield,
  AlertTriangle, CheckCircle2, XCircle, Camera, Loader2, Euro,
  Smartphone, Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = {
  creado: 'bg-muted text-foreground',
  enviado: 'bg-blue-100 text-blue-800',
  aceptado: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
  reparado: 'bg-emerald-100 text-emerald-800',
  cancelado: 'bg-muted text-foreground/70',
};

const euroFmt = (v) => v == null ? '—' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);
const daysFmt = (v) => v == null ? '—' : `${v.toFixed(1)} días`;

function Stat({ icon: Icon, label, value, sub, color = 'text-slate-900', testid }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniBar({ data, max, colorClass = 'bg-blue-500' }) {
  return (
    <div className="flex items-end gap-0.5 h-16">
      {data.map((d, i) => (
        <div key={i}
             title={`${d.fecha || d.mes}: ${d.count}`}
             style={{ height: `${Math.max(4, (d.count / Math.max(1, max)) * 100)}%` }}
             className={`flex-1 ${colorClass} rounded-t opacity-80 hover:opacity-100`} />
      ))}
    </div>
  );
}

export default function InsuramaKPIsTab() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const fetchKpis = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const { data } = await api.get('/insurama/kpis', { params });
      setKpis(data);
    } catch (e) {
      toast.error('No se pudieron cargar los KPIs');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/insurama/kpis/refresh');
      if (data.status === 'already_running') {
        toast.info('Ya hay una sincronización en curso...');
      } else {
        toast.success('Sincronización lanzada. Espera unos segundos...');
      }
      // Polling hasta que termine (máx ~90s)
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: status } = await api.get('/insurama/kpis/cache-status');
          if (status.sync_status === 'ok') {
            toast.success(`Sincronizadas ${status.total_cached} órdenes`);
            await fetchKpis();
            return;
          }
          if (status.sync_status === 'error') {
            toast.error(`Error en sync: ${status.sync_error || 'desconocido'}`);
            return;
          }
        } catch (e) {
          /* ignore */
        }
      }
      toast.warning('La sincronización está tardando más de lo normal. Refresca más tarde.');
      await fetchKpis();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error refrescando';
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchKpis(); /* eslint-disable-next-line */ }, []);

  if (loading && !kpis) {
    return (
      <Card><CardContent className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando KPIs…
      </CardContent></Card>
    );
  }

  const total = kpis?.total ?? 0;
  const sinDatos = total === 0;

  return (
    <div className="space-y-4" data-testid="insurama-kpis-panel">
      {/* Cabecera */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            KPIs del taller (datos Insurama)
          </h3>
          {kpis?.meta?.last_sync && (
            <p className="text-xs text-muted-foreground mt-1">
              Última sincronización: {new Date(kpis.meta.last_sync).toLocaleString('es-ES')}
              {kpis.meta.cache_age_minutes != null && ` · hace ${kpis.meta.cache_age_minutes} min`}
              {kpis.meta.total_cached != null && ` · ${kpis.meta.total_cached} órdenes en caché`}
            </p>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                   className="w-[140px]" data-testid="kpi-desde" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                   className="w-[140px]" data-testid="kpi-hasta" />
          </div>
          <Button variant="outline" onClick={fetchKpis} data-testid="kpi-aplicar">
            Aplicar filtro
          </Button>
          <Button onClick={handleRefresh} disabled={refreshing}
                  className="bg-blue-600 hover:bg-blue-700" data-testid="kpi-refresh">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sincronizar con Sumbroker
          </Button>
        </div>
      </div>

      {sinDatos && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <p className="text-foreground">
              {kpis?.mensaje || 'Sin datos en caché. Pulsa "Sincronizar con Sumbroker" para cargar tus 973 órdenes.'}
            </p>
          </CardContent>
        </Card>
      )}

      {!sinDatos && (
        <>
          {/* Fila 1: Volumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="kpi-volumen-row">
            <Stat icon={Package} label="Total órdenes" value={total} sub="en el rango filtrado" />
            <Stat icon={TrendingUp} label="Tipo reparación" value={kpis.volumen.tipo_reparacion}
                  sub={`${((kpis.volumen.tipo_reparacion / total) * 100).toFixed(0)}% del total`} />
            <Stat icon={Package} label="Tipo sustitución" value={kpis.volumen.tipo_sustitucion}
                  sub={`${((kpis.volumen.tipo_sustitucion / total) * 100).toFixed(0)}% del total`} />
            <Stat icon={Shield} label="En garantía" value={kpis.volumen.en_garantia}
                  color="text-amber-600"
                  sub={`${((kpis.volumen.en_garantia / total) * 100).toFixed(0)}% del total`} />
            <Stat icon={XCircle} label="Devoluciones" value={kpis.volumen.devoluciones}
                  color="text-red-600"
                  sub={`${((kpis.volumen.devoluciones / total) * 100).toFixed(1)}% del total`} />
            <Stat icon={AlertTriangle} label="Reparos (>1)" value={kpis.volumen.reparos}
                  color="text-orange-600"
                  sub="claims con >1 presupuesto" />
            <Stat icon={XCircle} label="Cancelados" value={kpis.volumen.cancelados}
                  color="text-foreground/70"
                  sub={`${((kpis.volumen.cancelados / total) * 100).toFixed(0)}% del total`} />
          </div>

          {/* Fila 2: Economía + Conversión */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card data-testid="kpi-economia-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Euro className="w-4 h-4 text-green-600" /> Economía
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <Stat label="Ticket medio" value={euroFmt(kpis.economia.ticket_medio_eur)}
                      sub={`sobre ${kpis.economia.muestras} órdenes con importe`} />
                <Stat label="Volumen facturado" value={euroFmt(kpis.economia.volumen_eur)}
                      color="text-green-700" sub="suma de todos los importes" />
                <Stat label="Valor productos" value={euroFmt(kpis.economia.valor_productos_reparados_eur)}
                      sub="suma de PVP de los móviles reparados" />
              </CardContent>
            </Card>
            <Card data-testid="kpi-conversion-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" /> Conversión de presupuestos
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-4 gap-3">
                <Stat label="Enviados" value={kpis.conversion.enviados} />
                <Stat label="Aceptados" value={kpis.conversion.aceptados} color="text-green-700"
                      sub={`${kpis.conversion.pct_aceptacion}% aceptación`} />
                <Stat label="Rechazados" value={kpis.conversion.rechazados} color="text-red-600"
                      sub={`${kpis.conversion.pct_rechazo}% rechazo`} />
                <div className="rounded-lg border bg-green-50 p-4">
                  <div className="text-xs text-muted-foreground uppercase">Ratio aceptación</div>
                  <div className="text-3xl font-extrabold text-green-700 mt-1">
                    {kpis.conversion.pct_aceptacion}%
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fila 3: Lead times */}
          <Card data-testid="kpi-leadtimes-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" /> Tiempos del flujo (media en días)
              </CardTitle>
              <CardDescription>
                Basado en las fechas oficiales que Sumbroker registra en cada orden.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Creado → Aceptado" value={daysFmt(kpis.lead_times_dias.creado_a_aceptado)} />
              <Stat label="Aceptado → Reparado" value={daysFmt(kpis.lead_times_dias.aceptado_a_reparado)} />
              <Stat label="Reparado → Enviado" value={daysFmt(kpis.lead_times_dias.reparado_a_enviado)} />
              <Stat label="Enviado → Entregado" value={daysFmt(kpis.lead_times_dias.enviado_a_entregado)} />
              <Stat label="Ciclo total medio" value={daysFmt(kpis.lead_times_dias.total_ciclo_medio)}
                    color="text-indigo-700" sub="Creado → Entregado" />
            </CardContent>
          </Card>

          {/* Fila 4: Órdenes paradas + Fotos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card data-testid="kpi-atascadas-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" /> Órdenes paradas (sin cerrar)
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <Stat label="≥7 días" value={kpis.atascadas['7d']} color="text-amber-600" />
                <Stat label="≥14 días" value={kpis.atascadas['14d']} color="text-orange-600" />
                <Stat label="≥30 días" value={kpis.atascadas['30d']} color="text-red-700" />
              </CardContent>
            </Card>
            <Card data-testid="kpi-fotos-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4 text-purple-600" /> Validación de fotos por Insurama
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Fotos recepción (pickup)</span>
                    <span>{kpis.fotos.pickup_validadas_por_insurama} / {kpis.fotos.pickup_subidas}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full"
                         style={{ width: `${Math.round((kpis.fotos.pickup_validadas_por_insurama / Math.max(1, kpis.fotos.pickup_subidas)) * 100)}%` }} />
                  </div>
                  {kpis.fotos.pickup_validacion_pendiente > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      {kpis.fotos.pickup_validacion_pendiente} pendientes de validación
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Fotos reparación (repair)</span>
                    <span>{kpis.fotos.repair_validadas_por_insurama} / {kpis.fotos.repair_subidas}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full"
                         style={{ width: `${Math.round((kpis.fotos.repair_validadas_por_insurama / Math.max(1, kpis.fotos.repair_subidas)) * 100)}%` }} />
                  </div>
                  {kpis.fotos.repair_validacion_pendiente > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      {kpis.fotos.repair_validacion_pendiente} pendientes de validación
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fila 5: Volumen temporal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" /> Volumen últimos 30 días
                </CardTitle>
              </CardHeader>
              <CardContent>
                {kpis.volumen.por_dia_ult_30d.length ? (
                  <>
                    <MiniBar data={kpis.volumen.por_dia_ult_30d}
                             max={Math.max(...kpis.volumen.por_dia_ult_30d.map(d => d.count))} />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>{kpis.volumen.por_dia_ult_30d[0]?.fecha}</span>
                      <span>{kpis.volumen.por_dia_ult_30d[kpis.volumen.por_dia_ult_30d.length - 1]?.fecha}</span>
                    </div>
                  </>
                ) : <p className="text-muted-foreground text-sm">Sin datos</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" /> Volumen últimos 12 meses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {kpis.volumen.por_mes_ult_12m.length ? (
                  <>
                    <MiniBar data={kpis.volumen.por_mes_ult_12m}
                             max={Math.max(...kpis.volumen.por_mes_ult_12m.map(d => d.count))}
                             colorClass="bg-indigo-500" />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>{kpis.volumen.por_mes_ult_12m[0]?.mes}</span>
                      <span>{kpis.volumen.por_mes_ult_12m[kpis.volumen.por_mes_ult_12m.length - 1]?.mes}</span>
                    </div>
                  </>
                ) : <p className="text-muted-foreground text-sm">Sin datos</p>}
              </CardContent>
            </Card>
          </div>

          {/* Fila 6: Dispositivos */}
          <Card data-testid="kpi-dispositivos-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-foreground" /> Dispositivos reparados
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-2">Top marcas</h4>
                <ul className="space-y-1 text-sm">
                  {kpis.dispositivos.top_marcas.slice(0, 8).map((m) => (
                    <li key={m.marca} className="flex justify-between">
                      <span>{m.marca}</span>
                      <Badge variant="outline">{m.count}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-2">Top modelos</h4>
                <ul className="space-y-1 text-sm">
                  {kpis.dispositivos.top_modelos.slice(0, 8).map((m, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="truncate">
                        <span className="text-muted-foreground/70">{m.brand}</span>{' '}{m.model}
                      </span>
                      <Badge variant="outline">{m.count}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-2">Tipos dispositivo</h4>
                <ul className="space-y-1 text-sm">
                  {Object.entries(kpis.dispositivos.tipos_dispositivo).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span>{k}</span>
                      <Badge variant="outline">{v}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Fila 7: Distribución de estados */}
          <Card data-testid="kpi-estados-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Distribución por estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(kpis.status_distribucion).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-sm py-1 px-2">
                    <span className="font-normal text-foreground/70 mr-1">{k}:</span>
                    <span className="font-bold">{v}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
